import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const ShiftList = () => {
  const [shifts, setShifts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleFilter, setScheduleFilter] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchAll = async () => {
    try {
      const [shRes, scRes] = await Promise.all([axios.get('/shifts'), axios.get('/schedules')]);
      setShifts(shRes.data || []);
      setSchedules(scRes.data || []);
    } catch { toast.error('Failed to load shifts'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleDelete = async id => {
    if (!window.confirm('Delete this shift? This will also remove related mappings and attendance records.')) return;
    setDeletingId(id);
    try {
      // Try to delete related records first, then the shift
      try { await axios.delete(`/shifts/${id}/mapping`); } catch (_) {}
      try { await axios.delete(`/shifts/${id}/attendance`); } catch (_) {}
      await axios.delete(`/shifts/${id}`);
      toast.success('Shift deleted successfully');
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || '';
      if (err?.response?.status === 409 || msg) {
        toast.error(`Delete failed: ${msg || 'Shift has related records that could not be removed.'}`);
      } else if (err?.response?.status === 404) {
        toast.error('Shift not found.');
      } else if (err?.response?.status === 403) {
        toast.error('You do not have permission to delete this shift.');
      } else {
        toast.error('Delete failed. Please try again or contact support.');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = scheduleFilter ? shifts.filter(s => String(s.schedule_id) === scheduleFilter) : shifts;
  const getScheduleName = id => schedules.find(s => s.id === id)?.exam_name || `Schedule #${id}`;
  const fmt = t => { if (!t) return '—'; const [h,m]=t.split(':'); const hr=parseInt(h); return `${hr%12||12}:${m} ${hr>=12?'PM':'AM'}`; };
  const canEdit = ['super_admin','system_admin'].includes(user?.role);

  if (loading) return <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh', color:'#666', fontSize:'18px' }}>Loading shifts...</div>;

  return (
    <div style={{ padding:'32px', backgroundColor:'#f5f6fa', minHeight:'100vh' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
        <div>
          <h2 style={{ color:'#1a237e', margin:0, fontSize:'26px', fontWeight:700 }}>Shifts</h2>
          <p style={{ color:'#888', margin:'4px 0 0', fontSize:'14px' }}>{shifts.length} total shifts</p>
        </div>
        {canEdit && (
          <button onClick={() => navigate('/shifts/add')}
            style={{ backgroundColor:'#1a237e', color:'white', border:'none', padding:'10px 20px', borderRadius:'8px', cursor:'pointer', fontWeight:600, fontSize:'14px' }}>
            + Add Shift
          </button>
        )}
      </div>

      <div style={{ marginBottom:'20px' }}>
        <select value={scheduleFilter} onChange={e => setScheduleFilter(e.target.value)}
          style={{ padding:'10px 14px', border:'1px solid #ddd', borderRadius:'8px', fontSize:'14px', backgroundColor:'white', minWidth:'260px' }}>
          <option value="">All Schedules</option>
          {schedules.map(s => <option key={s.id} value={s.id}>{s.exam_name}</option>)}
        </select>
      </div>

      <div style={{ backgroundColor:'white', borderRadius:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', overflow:'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign:'center', color:'#aaa', padding:'60px 0' }}>
            <div style={{ fontSize:'40px', marginBottom:'12px' }}>🕐</div>
            No shifts found.
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ backgroundColor:'#f0f4ff' }}>
                {['#','Schedule','Date','Shift','Time','Students','Rate (₹)','Holiday','Actions'].map(h => (
                  <th key={h} style={{ padding:'13px 14px', textAlign:'left', fontSize:'12px', color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr key={s.id} style={{ borderBottom:'1px solid #f0f0f0' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor='#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor=''}>
                  <td style={{ padding:'13px 14px', color:'#aaa' }}>{i+1}</td>
                  <td style={{ padding:'13px 14px', color:'#1a237e', fontWeight:600, fontSize:'13px' }}>{getScheduleName(s.schedule_id)}</td>
                  <td style={{ padding:'13px 14px', color:'#555', fontSize:'13px' }}>{s.shift_date}</td>
                  <td style={{ padding:'13px 14px' }}>
                    <span style={{ backgroundColor:'#e3f2fd', color:'#1565c0', padding:'3px 10px', borderRadius:'12px', fontSize:'12px', fontWeight:600 }}>
                      {s.shift_name || `Shift ${s.shift_number}`}
                    </span>
                  </td>
                  <td style={{ padding:'13px 14px', color:'#555', fontSize:'13px' }}>{fmt(s.start_time)} – {fmt(s.end_time)}</td>
                  <td style={{ padding:'13px 14px', color:'#555', textAlign:'center' }}>{s.student_count ?? 0}</td>
                  <td style={{ padding:'13px 14px', color:'#2e7d32', fontWeight:600 }}>₹{s.remuneration_rate ?? '0.00'}</td>
                  <td style={{ padding:'13px 14px' }}>
                    <span style={{ backgroundColor:s.is_holiday?'#ffebee':'#e8f5e9', color:s.is_holiday?'#c62828':'#2e7d32', padding:'3px 10px', borderRadius:'12px', fontSize:'12px', fontWeight:600 }}>
                      {s.is_holiday ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style={{ padding:'13px 14px' }}>
                    <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                      <button onClick={() => navigate(`/shifts/${s.id}/mapping`)}
                        style={{ backgroundColor:'#e8f5e9', color:'#2e7d32', border:'none', padding:'5px 10px', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:600 }}>Mapping</button>
                      <button onClick={() => navigate(`/shifts/${s.id}/attendance`)}
                        style={{ backgroundColor:'#e3f2fd', color:'#1565c0', border:'none', padding:'5px 10px', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:600 }}>Attendance</button>
                      {canEdit && (
                        <button
                          onClick={() => handleDelete(s.id)}
                          disabled={deletingId === s.id}
                          style={{ backgroundColor: deletingId === s.id ? '#eee' : '#ffebee', color: deletingId === s.id ? '#aaa' : '#c62828', border:'none', padding:'5px 10px', borderRadius:'6px', cursor: deletingId === s.id ? 'not-allowed' : 'pointer', fontSize:'12px', fontWeight:600 }}>
                          {deletingId === s.id ? 'Deleting…' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ShiftList;